import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Loader2,
  AlertCircle,
  Trash2,
  CreditCard,
  BookOpen,
  FolderOpen,
  Plus,
  Save,
  CopyCheck,
  Search,
  ShieldCheck,
  UserRoundCheck,
  CalendarClock,
  RotateCcw,
  X,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'
import { adminApi, type AdminContent } from '@/api/admin'
import { handleApiError } from '@/api/client'
import { ContentPickerTree } from '@/components/curriculum-builder/ContentPickerTree'
import {
  lcContentFeeApi,
  CONTENT_FEE_LEVELS,
  type LcContentFeePlan,
  type LcContentFeePlanTemplate,
  type PlanTemplateApplyPreview,
  type SavePlanTemplateRequest,
  type AdminTestLearnerCandidate,
  type AdminTestEntitlement,
  type EffectivePlanCourse,
  type EffectivePlanCurriculum,
  type EffectivePlanTier,
} from '@/api/lcContentFee'

interface SelectedContent {
  id: number
  title: string
  level: 'curriculum' | 'course'
}

const NO_EXISTING_IDS = new Set<number>()

const PLANS_QK = (contentId: number) =>
  ['admin', 'lc-content-fee', 'plans', contentId] as const

const EFFECTIVE_PLANS_QK = ['admin', 'lc-content-fee', 'effective-plans'] as const
const TEMPLATES_QK = ['admin', 'lc-content-fee', 'templates'] as const
const TEMPLATE_PREVIEW_QK = (templateId: number, contentId: number, levels: string[]) =>
  ['admin', 'lc-content-fee', 'template-preview', templateId, contentId, levels.join(',')] as const
const TEST_LEARNERS_QK = (keyword: string) =>
  ['admin', 'lc-content-fee', 'test-learners', keyword] as const
const TEST_ENTITLEMENTS_QK = ['admin', 'lc-content-fee', 'test-entitlements'] as const

// ---------------------------------------------------------------------------
// 콘텐츠 선택기 (커리큘럼 / 코스 탭)
// ---------------------------------------------------------------------------

type PickerTab = 'curriculum' | 'course'

function ContentPicker({
  selected,
  onSelect,
}: {
  selected: SelectedContent | null
  onSelect: (content: SelectedContent) => void
}) {
  const [tab, setTab] = useState<PickerTab>('curriculum')

  const { data: curricula, isLoading: curriculaLoading } = useQuery({
    queryKey: ['admin-contents-tree', 'curriculum'],
    queryFn: () => adminApi.getContentsByLevel('curriculum'),
    enabled: tab === 'curriculum',
  })

  return (
    <div>
      <div className="flex gap-1 mb-3">
        <TabButton active={tab === 'curriculum'} onClick={() => setTab('curriculum')}>
          <BookOpen className="w-3.5 h-3.5" /> 커리큘럼
        </TabButton>
        <TabButton active={tab === 'course'} onClick={() => setTab('course')}>
          <FolderOpen className="w-3.5 h-3.5" /> 코스
        </TabButton>
      </div>

      <div className="max-h-[28rem] overflow-y-auto border border-slate-200 rounded-lg p-2">
        {tab === 'curriculum' ? (
          curriculaLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (curricula ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">커리큘럼이 없습니다.</p>
          ) : (
            <div className="text-sm">
              {(curricula ?? []).map((curr) => {
                const isSelected = selected?.id === curr.id
                return (
                  <button
                    key={curr.id}
                    type="button"
                    onClick={() => onSelect({ id: curr.id, title: curr.title, level: 'curriculum' })}
                    className={`w-full flex items-center gap-1.5 py-1.5 px-2 rounded text-left transition-colors ${
                      isSelected ? 'bg-amber-50 text-amber-900' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <BookOpen className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                    <span className="truncate flex-1">{curr.title}</span>
                  </button>
                )
              })}
            </div>
          )
        ) : (
          <ContentPickerTree
            childLevel="course"
            existingIds={NO_EXISTING_IDS}
            onPick={(content: AdminContent) =>
              onSelect({ id: content.id, title: content.title, level: 'course' })
            }
          />
        )}
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
        active ? 'bg-amber-500 text-slate-900' : 'text-slate-500 hover:bg-slate-100'
      }`}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// 과정별 적용 현황 (결제 견적과 같은 규칙: 코스 예외 우선, 없으면 과정 기본값)
// ---------------------------------------------------------------------------

const formatTier = (tier: EffectivePlanTier) =>
  `${tier.price.toLocaleString('ko-KR')}원 · ${tier.grantedLessons}레슨`

const tierSummary = (tiers: EffectivePlanTier[]) =>
  tiers.length === 0
    ? '플랜 없음'
    : tiers.map((tier) => `${tier.level} ${formatTier(tier)}`).join(' / ')

function EffectiveSourceBadge({ course }: { course: EffectivePlanCourse }) {
  if (course.source === 'NONE') return <Badge variant="destructive">플랜 없음</Badge>
  if (course.source === 'CURRICULUM') return <Badge variant="secondary">과정 기본값</Badge>
  if (course.missingLevels.length > 0) {
    return <Badge variant="destructive">코스 예외 · {course.missingLevels.join('·')} 없음</Badge>
  }
  if (course.sameAsCurriculum) return <Badge variant="outline">코스 예외 · 기본값과 같음</Badge>
  return <Badge>코스 예외</Badge>
}

export function EffectivePlanOverview({
  curricula,
  isLoading,
  isError,
  selectedId,
  onSelect,
}: {
  curricula: EffectivePlanCurriculum[]
  isLoading: boolean
  isError: boolean
  selectedId: number | null
  onSelect: (content: SelectedContent) => void
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-slate-900">과정별 적용 현황</h2>
        <p className="text-sm text-slate-500">
          원생이 듣는 코스에 코스 예외 플랜이 있으면 그 값을, 없으면 과정 기본값을 결제 견적에 씁니다. 이름을 누르면 편집합니다.
        </p>
      </div>
      {isLoading ? (
        <Loading />
      ) : isError ? (
        <LoadError />
      ) : curricula.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-400">설정된 플랜이 없습니다.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>과정 / 코스</TableHead>
              <TableHead>적용</TableHead>
              {CONTENT_FEE_LEVELS.map((level) => (
                <TableHead key={level} className="text-right">{level}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {curricula.map((curriculum) => (
              <EffectiveCurriculumRows
                key={curriculum.curriculumId}
                curriculum={curriculum}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

function EffectiveCurriculumRows({
  curriculum,
  selectedId,
  onSelect,
}: {
  curriculum: EffectivePlanCurriculum
  selectedId: number | null
  onSelect: (content: SelectedContent) => void
}) {
  const tierCells = (tiers: EffectivePlanTier[]) =>
    CONTENT_FEE_LEVELS.map((level) => {
      const tier = tiers.find((item) => item.level === level)
      return (
        <TableCell key={level} className="text-right tabular-nums">
          {tier ? formatTier(tier) : <span className="text-slate-400">—</span>}
        </TableCell>
      )
    })

  return (
    <>
      <TableRow className="bg-slate-50">
        <TableCell className="font-semibold">
          <button
            type="button"
            onClick={() =>
              onSelect({ id: curriculum.curriculumId, title: curriculum.title, level: 'curriculum' })
            }
            className={`text-left hover:underline ${selectedId === curriculum.curriculumId ? 'text-amber-700' : ''}`}
          >
            {curriculum.title}
          </button>
        </TableCell>
        <TableCell>
          {curriculum.tiers.length > 0 ? (
            <Badge variant="secondary">과정 기본값</Badge>
          ) : (
            <Badge variant="destructive">기본값 없음</Badge>
          )}
        </TableCell>
        {tierCells(curriculum.tiers)}
      </TableRow>
      {curriculum.courses.length === 0 ? (
        <TableRow>
          <TableCell colSpan={2 + CONTENT_FEE_LEVELS.length} className="pl-6 text-slate-400">
            소속 코스가 없습니다.
          </TableCell>
        </TableRow>
      ) : (
        curriculum.courses.map((course) => (
          <TableRow key={course.courseId}>
            <TableCell className="pl-6">
              <button
                type="button"
                onClick={() => onSelect({ id: course.courseId, title: course.title, level: 'course' })}
                className={`text-left hover:underline ${selectedId === course.courseId ? 'text-amber-700' : ''}`}
              >
                {course.title}
              </button>
            </TableCell>
            <TableCell><EffectiveSourceBadge course={course} /></TableCell>
            {tierCells(course.tiers)}
          </TableRow>
        ))
      )}
    </>
  )
}

/** 선택 콘텐츠의 상속 관계를 설명한다. 코스는 속한 과정의 기본값과 예외 여부, 과정은 코스 예외 목록. */
export function PlanInheritanceNotice({
  content,
  plans,
  curricula,
  onClearCourseException,
  clearing,
}: {
  content: SelectedContent
  plans: LcContentFeePlan[]
  curricula: EffectivePlanCurriculum[]
  onClearCourseException: () => void
  clearing: boolean
}) {
  if (content.level === 'curriculum') {
    const curriculum = curricula.find((item) => item.curriculumId === content.id)
    const exceptions = curriculum?.courses.filter((course) => course.source === 'COURSE') ?? []
    return (
      <Alert className="mb-4">
        <AlertCircle />
        <AlertTitle>과정 기본값</AlertTitle>
        <AlertDescription>
          이 과정의 모든 코스에 적용됩니다. 코스 예외가 있는 코스만 예외 값이 우선합니다.
          {exceptions.length > 0
            ? ` 코스 예외: ${exceptions.map((course) => course.title).join(', ')}`
            : ' 현재 코스 예외는 없습니다.'}
        </AlertDescription>
      </Alert>
    )
  }

  const parents = curricula.filter((curriculum) =>
    curriculum.courses.some((course) => course.courseId === content.id),
  )
  const activeLevels = new Set(plans.filter((plan) => plan.active).map((plan) => plan.level))
  const hasException = activeLevels.size > 0
  const missingLevels = CONTENT_FEE_LEVELS.filter((level) => !activeLevels.has(level))
  const sameAsAllParents =
    hasException &&
    parents.length > 0 &&
    parents.every((curriculum) =>
      curriculum.courses.find((course) => course.courseId === content.id)?.sameAsCurriculum,
    )
  const parentList =
    parents.length === 0 ? (
      <p>플랜이 있는 과정에 속하지 않은 코스입니다.</p>
    ) : (
      <ul className="mt-1 list-disc pl-4">
        {parents.map((curriculum) => (
          <li key={curriculum.curriculumId}>
            {curriculum.title}: {tierSummary(curriculum.tiers)}
          </li>
        ))}
      </ul>
    )

  if (!hasException) {
    return (
      <Alert className="mb-4">
        <AlertCircle />
        <AlertTitle>과정 기본값 사용 중</AlertTitle>
        <AlertDescription>
          이 코스는 속한 과정의 기본값을 씁니다. 여기에 저장하면 코스 예외가 되어 과정 기본값 대신 적용되므로 L3·L4·L5를 모두 저장하세요.
          {parentList}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <Alert className="mb-4" variant={missingLevels.length > 0 ? 'destructive' : 'default'}>
      <AlertCircle />
      <AlertTitle>코스 예외 적용 중</AlertTitle>
      <AlertDescription>
        이 코스를 듣는 원생에게는 과정 기본값 대신 아래 값이 적용됩니다.
        {missingLevels.length > 0
          ? ` ${missingLevels.join('·')} 레벨이 없어 이 코스에서는 해당 레벨을 결제할 수 없습니다. 세 레벨을 모두 저장하세요.`
          : ''}
        {sameAsAllParents ? ' 속한 과정의 기본값과 같으므로 예외를 해제해도 견적이 바뀌지 않습니다.' : ''}
        {parentList}
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          disabled={clearing}
          onClick={onClearCourseException}
        >
          {clearing ? <Loader2 className="animate-spin" /> : null}
          예외 해제(과정 기본값 사용)
        </Button>
      </AlertDescription>
    </Alert>
  )
}

// ---------------------------------------------------------------------------
// 플랜 에디터 (선택된 콘텐츠 × 레벨)
// ---------------------------------------------------------------------------

interface RowDraft {
  price: string
  grantedLessons: string
  active: boolean
}

const EMPTY_DRAFT: RowDraft = { price: '', grantedLessons: '', active: true }

function PlanEditor({
  content,
  curricula,
}: {
  content: SelectedContent
  curricula: EffectivePlanCurriculum[]
}) {
  const queryClient = useQueryClient()
  const queryKey = PLANS_QK(content.id)

  const { data: plans, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => lcContentFeeApi.getPlans(content.id),
    select: (res) => res.data.data ?? [],
  })

  const planByLevel = useMemo(() => {
    const m = new Map<string, LcContentFeePlan>()
    for (const p of plans ?? []) m.set(p.level, p)
    return m
  }, [plans])

  // 사용자가 직접 수정한(=touched) 레벨. 서버 refetch 시 이 레벨들의 draft는
  // 덮어쓰지 않아 미저장 입력이 보존된다. 저장/삭제 성공 시 해당 레벨을 다시
  // untouched로 돌려 서버 값과 재동기화한다.
  const [draft, setDraft] = useState<Record<string, RowDraft>>({})
  const [dirtyLevels, setDirtyLevels] = useState<Set<string>>(new Set())

  useEffect(() => {
    setDraft((prev) => {
      const next: Record<string, RowDraft> = {}
      for (const level of CONTENT_FEE_LEVELS) {
        if (dirtyLevels.has(level) && prev[level]) {
          next[level] = prev[level]
          continue
        }
        const existing = planByLevel.get(level)
        next[level] = existing
          ? {
              price: String(existing.price),
              grantedLessons: String(existing.grantedLessons),
              active: existing.active,
            }
          : { ...EMPTY_DRAFT }
      }
      return next
    })
  }, [planByLevel, dirtyLevels])

  const clearDirty = (level: string) =>
    setDirtyLevels((prev) => {
      if (!prev.has(level)) return prev
      const next = new Set(prev)
      next.delete(level)
      return next
    })

  const upsertMutation = useMutation({
    mutationFn: (body: {
      level: string
      price: number
      grantedLessons: number
      active: boolean
    }) => lcContentFeeApi.upsertPlan({ contentId: content.id, ...body }),
    onSuccess: (_data, variables) => {
      clearDirty(variables.level)
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: EFFECTIVE_PLANS_QK })
      toast.success('플랜을 저장했습니다.')
    },
    onError: () => toast.error('플랜 저장에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (vars: { id: number; level: string }) =>
      lcContentFeeApi.deletePlan(vars.id),
    onSuccess: (_data, variables) => {
      clearDirty(variables.level)
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: EFFECTIVE_PLANS_QK })
      toast.success('플랜을 삭제했습니다.')
    },
    onError: () => toast.error('플랜 삭제에 실패했습니다.'),
  })

  const clearExceptionMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      for (const id of ids) await lcContentFeeApi.deletePlan(id)
    },
    onSuccess: () => {
      setDirtyLevels(new Set())
      toast.success('코스 예외를 해제했습니다. 과정 기본값이 적용됩니다.')
    },
    onError: () => toast.error('코스 예외 해제에 실패했습니다.'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: EFFECTIVE_PLANS_QK })
    },
  })

  if (isLoading) return <Loading />
  if (isError) return <LoadError />

  const currency = (plans ?? [])[0]?.currency ?? 'KRW'

  return (
    <div>
      <div className="mb-4">
        <p className="text-xs text-slate-500">선택된 콘텐츠</p>
        <p className="text-sm font-medium text-slate-800 break-all">
          #{content.id} · {content.title}
        </p>
      </div>

      <PlanInheritanceNotice
        content={content}
        plans={plans ?? []}
        curricula={curricula}
        clearing={clearExceptionMutation.isPending}
        onClearCourseException={() => {
          const ids = (plans ?? []).map((plan) => plan.id)
          if (ids.length === 0) return
          if (window.confirm(`${content.title} 코스 예외 플랜 ${ids.length}개를 삭제하고 과정 기본값을 적용할까요?`)) {
            clearExceptionMutation.mutate(ids)
          }
        }}
      />

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-2 pr-4 font-medium">레벨</th>
            <th className="py-2 pr-4 font-medium">가격 ({currency})</th>
            <th className="py-2 pr-4 font-medium">레슨수</th>
            <th className="py-2 pr-4 font-medium text-center">활성</th>
            <th className="py-2 font-medium text-right">작업</th>
          </tr>
        </thead>
        <tbody>
          {CONTENT_FEE_LEVELS.map((level) => {
            const row = draft[level] ?? EMPTY_DRAFT
            const existing = planByLevel.get(level)

            const priceNum = Number(row.price)
            const lessonsNum = Number(row.grantedLessons)
            const priceValid =
              row.price.trim() !== '' && Number.isFinite(priceNum) && priceNum >= 0
            const lessonsValid =
              row.grantedLessons.trim() !== '' &&
              Number.isInteger(lessonsNum) &&
              lessonsNum >= 0
            const canSave = priceValid && lessonsValid

            const isSavingRow =
              upsertMutation.isPending && upsertMutation.variables?.level === level
            const isDeletingRow =
              deleteMutation.isPending && deleteMutation.variables?.level === level

            const setRow = (patch: Partial<RowDraft>) => {
              setDraft((d) => ({ ...d, [level]: { ...(d[level] ?? EMPTY_DRAFT), ...patch } }))
              setDirtyLevels((prev) => (prev.has(level) ? prev : new Set(prev).add(level)))
            }

            return (
              <tr key={level} className="border-b border-slate-100">
                <td className="py-3 pr-4 font-medium text-slate-800">
                  {level}
                  {!existing && (
                    <span className="ml-2 text-[10px] text-slate-400">미설정</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={row.price}
                    onChange={(e) => setRow({ price: e.target.value })}
                    placeholder="0"
                    className="w-32 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </td>
                <td className="py-3 pr-4">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={row.grantedLessons}
                    onChange={(e) => setRow({ grantedLessons: e.target.value })}
                    placeholder="0"
                    className="w-28 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  />
                </td>
                <td className="py-3 pr-4 text-center">
                  <input
                    type="checkbox"
                    checked={row.active}
                    onChange={(e) => setRow({ active: e.target.checked })}
                    className="w-4 h-4 accent-amber-500"
                  />
                </td>
                <td className="py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    disabled={!canSave || isSavingRow}
                    onClick={() =>
                      upsertMutation.mutate({
                        level,
                        price: priceNum,
                        grantedLessons: lessonsNum,
                        active: row.active,
                      })
                    }
                    className="px-3 py-1.5 text-sm rounded-lg bg-amber-500 text-slate-900 font-medium hover:bg-amber-600 disabled:opacity-40"
                  >
                    저장
                  </button>
                  {existing && (
                    <button
                      type="button"
                      disabled={isDeletingRow}
                      onClick={() => {
                        if (window.confirm(`${level} 플랜을 삭제할까요?`)) {
                          deleteMutation.mutate({ id: existing.id, level })
                        }
                      }}
                      className="ml-2 inline-flex items-center gap-1 px-2 py-1.5 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" /> 삭제
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 템플릿 관리 + 선택 콘텐츠 적용
// ---------------------------------------------------------------------------

interface TemplateTierDraft {
  id?: number | null
  level: string
  price: string
  currency: string
  grantedLessons: string
  active: boolean
}

interface TemplateDraft {
  id?: number
  name: string
  description: string
  active: boolean
  tiers: TemplateTierDraft[]
}

const createEmptyTemplateTier = (level: string): TemplateTierDraft => ({
  level,
  price: '0',
  currency: 'KRW',
  grantedLessons: '0',
  active: true,
})

const createEmptyTemplateDraft = (): TemplateDraft => ({
  name: '',
  description: '',
  active: true,
  tiers: CONTENT_FEE_LEVELS.map(createEmptyTemplateTier),
})

const templateToDraft = (template: LcContentFeePlanTemplate): TemplateDraft => {
  const tierByLevel = new Map(template.tiers.map((tier) => [tier.level, tier]))
  return {
    id: template.id,
    name: template.name,
    description: template.description ?? '',
    active: template.active,
    tiers: CONTENT_FEE_LEVELS.map((level) => {
      const tier = tierByLevel.get(level)
      return tier
        ? {
            id: tier.id,
            level,
            price: String(tier.price),
            currency: tier.currency,
            grantedLessons: String(tier.grantedLessons),
            active: tier.active,
          }
        : createEmptyTemplateTier(level)
    }),
  }
}

const formatPlanSummary = (
  plan: Pick<LcContentFeePlan, 'price' | 'currency' | 'grantedLessons' | 'active'>,
) =>
  `${plan.price} ${plan.currency} / ${plan.grantedLessons}회 / ${plan.active ? '활성' : '비활성'}`

function TemplateManager({ selectedContent }: { selectedContent: SelectedContent | null }) {
  const queryClient = useQueryClient()
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [selectedLevels, setSelectedLevels] = useState<string[]>([...CONTENT_FEE_LEVELS])
  const [draft, setDraft] = useState<TemplateDraft>(() => createEmptyTemplateDraft())
  const [preview, setPreview] = useState<PlanTemplateApplyPreview | null>(null)

  const { data: templates = [], isLoading, isError } = useQuery({
    queryKey: TEMPLATES_QK,
    queryFn: () => lcContentFeeApi.getTemplates(),
    select: (res) => res.data.data ?? [],
  })

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  )

  useEffect(() => {
    setPreview(null)
  }, [selectedContent?.id])

  const saveBody = useMemo<SavePlanTemplateRequest | null>(() => {
    const name = draft.name.trim()
    if (!name) return null

    const tiers = draft.tiers.map((tier) => {
      const price = Number(tier.price)
      const grantedLessons = Number(tier.grantedLessons)
      return {
        id: tier.id,
        level: tier.level,
        price,
        currency: tier.currency.trim().toUpperCase(),
        grantedLessons,
        active: tier.active,
      }
    })

    const validTiers = tiers.every(
      (tier) =>
        Number.isFinite(tier.price) &&
        tier.price >= 0 &&
        Number.isInteger(tier.grantedLessons) &&
        tier.grantedLessons >= 0 &&
        /^[A-Z]{3}$/.test(tier.currency),
    )
    if (!validTiers) return null

    return {
      name,
      description: draft.description.trim() || null,
      active: draft.active,
      tiers,
    }
  }, [draft])

  const resetPreview = () => setPreview(null)

  const saveMutation = useMutation({
    mutationFn: (body: SavePlanTemplateRequest & { id?: number }) => {
      const { id, ...request } = body
      return id
        ? lcContentFeeApi.updateTemplate(id, request)
        : lcContentFeeApi.createTemplate(request)
    },
    onSuccess: (res) => {
      const saved = res.data.data
      queryClient.invalidateQueries({ queryKey: TEMPLATES_QK })
      if (saved) {
        setSelectedTemplateId(saved.id)
        setDraft(templateToDraft(saved))
      }
      resetPreview()
      toast.success('템플릿을 저장했습니다.')
    },
    onError: () => toast.error('템플릿 저장에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => lcContentFeeApi.deleteTemplate(id),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_QK })
      if (selectedTemplateId === id) {
        setSelectedTemplateId(null)
        setDraft(createEmptyTemplateDraft())
        resetPreview()
      }
      toast.success('템플릿을 삭제했습니다.')
    },
    onError: () => toast.error('템플릿 삭제에 실패했습니다.'),
  })

  const previewMutation = useMutation({
    mutationFn: ({ templateId, contentId, levels }: { templateId: number; contentId: number; levels: string[] }) =>
      lcContentFeeApi.previewTemplateApply(templateId, { contentId, levels }),
    onSuccess: (res, variables) => {
      const data = res.data.data ?? null
      setPreview(data)
      queryClient.setQueryData(
        TEMPLATE_PREVIEW_QK(variables.templateId, variables.contentId, variables.levels),
        data,
      )
    },
    onError: () => toast.error('비교표를 불러오지 못했습니다.'),
  })

  const applyMutation = useMutation({
    mutationFn: ({ templateId, contentId, levels }: { templateId: number; contentId: number; levels: string[] }) =>
      lcContentFeeApi.applyTemplate(templateId, { contentId, levels }),
    onSuccess: (res, variables) => {
      setPreview(res.data.data ?? null)
      queryClient.invalidateQueries({ queryKey: PLANS_QK(variables.contentId) })
      queryClient.invalidateQueries({ queryKey: EFFECTIVE_PLANS_QK })
      queryClient.invalidateQueries({ queryKey: TEMPLATES_QK })
      toast.success('템플릿을 적용했습니다.')
    },
    onError: () => toast.error('템플릿 적용에 실패했습니다.'),
  })

  const updateTier = (level: string, patch: Partial<TemplateTierDraft>) => {
    setDraft((current) => ({
      ...current,
      tiers: current.tiers.map((tier) =>
        tier.level === level ? { ...tier, ...patch } : tier,
      ),
    }))
    resetPreview()
  }

  const toggleLevel = (level: string) => {
    setSelectedLevels((current) => {
      const next = current.includes(level)
        ? current.filter((item) => item !== level)
        : [...current, level]
      return CONTENT_FEE_LEVELS.filter((item) => next.includes(item))
    })
    resetPreview()
  }

  const selectTemplate = (template: LcContentFeePlanTemplate) => {
    setSelectedTemplateId(template.id)
    setDraft(templateToDraft(template))
    setSelectedLevels([...CONTENT_FEE_LEVELS])
    resetPreview()
  }

  const startNewTemplate = () => {
    setSelectedTemplateId(null)
    setDraft(createEmptyTemplateDraft())
    setSelectedLevels([...CONTENT_FEE_LEVELS])
    resetPreview()
  }

  const canPreview =
    selectedContent !== null &&
    selectedTemplate !== null &&
    selectedLevels.length > 0 &&
    !previewMutation.isPending

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-semibold text-slate-900">템플릿</h2>
          <button
            type="button"
            onClick={startNewTemplate}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <Plus className="w-4 h-4" /> 새 템플릿
          </button>
        </div>

        <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200">
          {isLoading ? (
            <div className="px-3 py-4">
              <Loading />
            </div>
          ) : isError ? (
            <div className="px-3 py-4">
              <LoadError />
            </div>
          ) : templates.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-400">템플릿이 없습니다.</p>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => selectTemplate(template)}
                className={`w-full border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 ${
                  selectedTemplateId === template.id
                    ? 'bg-amber-50 text-amber-900'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{template.name}</span>
                  <span className={`shrink-0 text-[11px] ${template.active ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {template.active ? '활성' : '비활성'}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <input
          type="text"
          value={draft.name}
          onChange={(e) => {
            setDraft((current) => ({ ...current, name: e.target.value }))
            resetPreview()
          }}
          placeholder="템플릿 이름"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500"
        />
        <textarea
          value={draft.description}
          onChange={(e) => {
            setDraft((current) => ({ ...current, description: e.target.value }))
            resetPreview()
          }}
          placeholder="설명"
          rows={2}
          className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500"
        />
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(e) => {
              setDraft((current) => ({ ...current, active: e.target.checked }))
              resetPreview()
            }}
            className="w-4 h-4 accent-amber-500"
          />
          활성 템플릿
        </label>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[21rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                <th className="py-2 pr-2 font-medium">레벨</th>
                <th className="py-2 pr-2 font-medium">가격</th>
                <th className="py-2 pr-2 font-medium">회차</th>
                <th className="py-2 pr-2 font-medium">통화</th>
                <th className="py-2 font-medium text-center">활성</th>
              </tr>
            </thead>
            <tbody>
              {draft.tiers.map((tier) => (
                <tr key={tier.level} className="border-b border-slate-100 last:border-b-0">
                  <td className="py-2 pr-2 font-medium text-slate-800">{tier.level}</td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      min={0}
                      value={tier.price}
                      onChange={(e) => updateTier(tier.level, { price: e.target.value })}
                      className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 focus:ring-2 focus:ring-amber-500"
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="number"
                      min={0}
                      value={tier.grantedLessons}
                      onChange={(e) => updateTier(tier.level, { grantedLessons: e.target.value })}
                      className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 focus:ring-2 focus:ring-amber-500"
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <input
                      type="text"
                      maxLength={3}
                      value={tier.currency}
                      onChange={(e) => updateTier(tier.level, { currency: e.target.value.toUpperCase() })}
                      className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 uppercase focus:ring-2 focus:ring-amber-500"
                    />
                  </td>
                  <td className="py-2 text-center">
                    <input
                      type="checkbox"
                      checked={tier.active}
                      onChange={(e) => updateTier(tier.level, { active: e.target.checked })}
                      className="w-4 h-4 accent-amber-500"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!saveBody || saveMutation.isPending}
            onClick={() => saveBody && saveMutation.mutate({ ...saveBody, id: draft.id })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-amber-600 disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> 저장
          </button>
          {draft.id && (
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const templateId = draft.id
                if (
                  templateId !== undefined &&
                  window.confirm('템플릿을 삭제할까요? 이미 적용된 콘텐츠 플랜은 변경되지 않습니다.')
                ) {
                  deleteMutation.mutate(templateId)
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" /> 삭제
            </button>
          )}
        </div>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <p className="mb-2 text-xs font-medium text-slate-500">적용 레벨</p>
        <div className="flex flex-wrap gap-2">
          {CONTENT_FEE_LEVELS.map((level) => (
            <label
              key={level}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={selectedLevels.includes(level)}
                onChange={() => toggleLevel(level)}
                className="w-4 h-4 accent-amber-500"
              />
              {level}
            </label>
          ))}
        </div>

        <button
          type="button"
          disabled={!canPreview}
          onClick={() =>
            selectedTemplate &&
            selectedContent &&
            previewMutation.mutate({
              templateId: selectedTemplate.id,
              contentId: selectedContent.id,
              levels: selectedLevels,
            })
          }
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          <CopyCheck className="w-4 h-4" /> 비교 보기
        </button>

        {!selectedContent && (
          <p className="mt-3 text-sm text-slate-400">콘텐츠를 선택하면 적용할 수 있습니다.</p>
        )}

        {preview && (
          <div className="mt-4 flex flex-col gap-3">
            {preview.courseOverrideWarning && (
              <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
                <span>코스 플랜은 결제 견적에서 커리큘럼 플랜보다 우선 적용됩니다.</span>
              </div>
            )}
            {preview.zeroValueWarning && (
              <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
                <span>0원 또는 0회차 값이 포함되어 있습니다. 의도한 값인지 확인하세요.</span>
              </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[23rem] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                    <th className="py-2 pr-3 pl-3 font-medium">레벨</th>
                    <th className="py-2 pr-3 font-medium">현재</th>
                    <th className="py-2 pr-3 font-medium">적용 후</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((change) => (
                    <tr key={change.level} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-2 pr-3 pl-3 font-medium text-slate-800">{change.level}</td>
                      <td className="py-2 pr-3 text-slate-600">
                        {change.before ? formatPlanSummary(change.before) : '미설정'}
                      </td>
                      <td className="py-2 pr-3 text-slate-900">
                        {formatPlanSummary(change.after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              disabled={applyMutation.isPending}
              onClick={() =>
                selectedTemplate &&
                selectedContent &&
                applyMutation.mutate({
                  templateId: selectedTemplate.id,
                  contentId: selectedContent.id,
                  levels: preview.levels,
                })
              }
              className="inline-flex w-fit items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40"
            >
              적용
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// 상용 테스트 학습권 (superadmin only)
// ---------------------------------------------------------------------------

const candidateKey = (candidate: AdminTestLearnerCandidate) =>
  `${candidate.organizationId}:${candidate.userId}`

const UNAVAILABLE_REASON_LABELS: Record<string, string> = {
  PLAN_NOT_FOUND: '플랜 없음',
  COURSE_NOT_DERIVABLE: '과정 판정 불가',
}

const unavailableLabel = (reason: string) => UNAVAILABLE_REASON_LABELS[reason] ?? '발급 불가'

type CandidateTone = 'blocked' | 'replace' | 'new'

/** 후보 선택 가능 여부와 배지. 응답은 null 필드를 생략하므로 null 비교가 아니라 값 유무로 판정한다. */
export function candidateStatus(
  candidate: AdminTestLearnerCandidate,
): { selectable: boolean; label: string; tone: CandidateTone } {
  if (candidate.activeEntitlementSource === 'PURCHASE') {
    return { selectable: false, label: '유료권 활성', tone: 'blocked' }
  }
  if (candidate.unavailableReason) {
    return { selectable: false, label: unavailableLabel(candidate.unavailableReason), tone: 'blocked' }
  }
  if (typeof candidate.contentId !== 'number' || candidate.plans.length === 0) {
    return { selectable: false, label: '과정 판정 불가', tone: 'blocked' }
  }
  if (candidate.activeEntitlementSource === 'ADMIN_TEST') {
    return { selectable: true, label: '테스트권 교체', tone: 'replace' }
  }
  return { selectable: true, label: '발급 가능', tone: 'new' }
}

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium' }).format(
    new Date(`${value}T00:00:00`),
  )

function TestEntitlementManager() {
  const queryClient = useQueryClient()
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [selectedCandidateKey, setSelectedCandidateKey] = useState<string | null>(null)
  const [selectedLevel, setSelectedLevel] = useState<string>('')
  const [grantReason, setGrantReason] = useState('')
  const [confirmationText, setConfirmationText] = useState('')
  const [grantAttemptId, setGrantAttemptId] = useState(() => crypto.randomUUID())
  const [revokeTarget, setRevokeTarget] = useState<AdminTestEntitlement | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  const learnersQuery = useQuery({
    queryKey: TEST_LEARNERS_QK(searchKeyword),
    queryFn: () => lcContentFeeApi.searchTestLearners(searchKeyword),
    select: (res) => res.data.data ?? [],
    enabled: searchKeyword.length >= 2,
  })
  const candidates = learnersQuery.data ?? []
  const selectedCandidate = candidates.find(
    (candidate) => candidateKey(candidate) === selectedCandidateKey,
  ) ?? null
  const eligiblePlans = selectedCandidate?.plans ?? []
  const selectedPlan = eligiblePlans.find((plan) => plan.level === selectedLevel) ?? null

  const entitlementsQuery = useQuery({
    queryKey: TEST_ENTITLEMENTS_QK,
    queryFn: () => lcContentFeeApi.getActiveTestEntitlements(),
    select: (res) => res.data.data ?? [],
  })

  const refreshAccessData = () => {
    queryClient.invalidateQueries({ queryKey: TEST_ENTITLEMENTS_QK })
    queryClient.invalidateQueries({ queryKey: ['admin', 'lc-content-fee', 'test-learners'] })
  }

  const grantMutation = useMutation({
    mutationFn: () => {
      if (!selectedCandidate || !selectedPlan || typeof selectedCandidate.contentId !== 'number') {
        throw new Error('발급 대상과 플랜을 선택해주세요.')
      }
      return lcContentFeeApi.grantTestEntitlement({
        organizationId: selectedCandidate.organizationId,
        userId: selectedCandidate.userId,
        contentId: selectedCandidate.contentId,
        level: selectedPlan.level,
        grantAttemptId,
        grantReason: grantReason.trim(),
        confirmationText: confirmationText.trim(),
      })
    },
    onSuccess: () => {
      refreshAccessData()
      setSelectedCandidateKey(null)
      setGrantReason('')
      setConfirmationText('')
      setGrantAttemptId(crypto.randomUUID())
      toast.success('테스트 학습권을 발급했습니다.')
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const revokeMutation = useMutation({
    mutationFn: ({ entitlementId, reason }: { entitlementId: number; reason: string }) =>
      lcContentFeeApi.revokeTestEntitlement(entitlementId, reason),
    onSuccess: () => {
      refreshAccessData()
      setRevokeTarget(null)
      setRevokeReason('')
      toast.success('테스트 학습권을 회수했습니다.')
    },
    onError: (error) => toast.error(handleApiError(error)),
  })

  const expectedConfirmation = selectedCandidate ? `GRANT ${selectedCandidate.userId}` : ''
  const canGrant =
    selectedCandidate !== null &&
    candidateStatus(selectedCandidate).selectable &&
    selectedPlan !== null &&
    grantReason.trim().length > 0 &&
    confirmationText.trim() === expectedConfirmation &&
    !grantMutation.isPending

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-slate-100 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.9)]">
      <div className="border-b border-slate-800 bg-[linear-gradient(110deg,rgba(245,158,11,0.16),transparent_48%)] px-6 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-2.5 text-amber-300">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold tracking-tight">상용 테스트 학습권</h2>
                <span className="rounded-full border border-red-400/30 bg-red-400/10 px-2 py-0.5 text-[11px] font-bold tracking-[0.12em] text-red-300">
                  {import.meta.env.PROD ? 'PRODUCTION' : import.meta.env.MODE.toUpperCase()}
                </span>
              </div>
              <p className="mt-1 max-w-3xl text-sm text-slate-400">
                결제 기록 없이 지정 원생에게만 35일 제한 학습권을 발급합니다. 유료 수강권은 변경되지 않습니다.
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900/80 px-3 py-2 text-right text-xs text-slate-400">
            <p className="font-medium text-slate-200">원생의 현재 과정 기준</p>
            <p>원생을 고르면 현재 코스·커리큘럼의 플랜 레벨이 표시됩니다.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-0 xl:grid-cols-[1.05fr_0.95fr]">
        <div className="border-b border-slate-800 p-6 xl:border-r xl:border-b-0">
          <div className="mb-5 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-xs font-bold text-slate-950">1</span>
            <h3 className="font-semibold">원생과 플랜 선택</h3>
          </div>

          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              const normalized = keyword.trim()
              if (normalized.length < 2) {
                toast.error('이름 또는 이메일을 2자 이상 입력해주세요.')
                return
              }
              setSearchKeyword(normalized)
              setSelectedCandidateKey(null)
              setSelectedLevel('')
            }}
          >
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="원생 이름 또는 이메일"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pr-3 pl-9 text-sm text-slate-100 outline-none placeholder:text-slate-600 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
              />
            </div>
            <button
              type="submit"
              disabled={learnersQuery.isFetching}
              className="rounded-lg bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-amber-300 disabled:opacity-50"
            >
              {learnersQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : '검색'}
            </button>
          </form>

          <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-slate-800 bg-slate-900/60">
            {searchKeyword.length < 2 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">검색어를 입력해주세요.</p>
            ) : learnersQuery.isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
            ) : learnersQuery.isError ? (
              <p className="px-4 py-8 text-center text-sm text-red-300">원생 검색에 실패했습니다.</p>
            ) : candidates.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">검색어와 일치하는 active LC 원생이 없습니다.</p>
            ) : (
              candidates.map((candidate) => {
                const key = candidateKey(candidate)
                const status = candidateStatus(candidate)
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={!status.selectable}
                    onClick={() => {
                      setSelectedCandidateKey(key)
                      setSelectedLevel(candidate.plans.length === 1 ? candidate.plans[0].level : '')
                      setConfirmationText('')
                      setGrantAttemptId(crypto.randomUUID())
                    }}
                    className={`flex w-full items-center justify-between gap-3 border-b border-slate-800 px-4 py-3 text-left last:border-b-0 ${
                      selectedCandidateKey === key
                        ? 'bg-amber-400/10 ring-1 ring-inset ring-amber-400/40'
                        : 'hover:bg-slate-800/70'
                    } disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-100">{candidate.fullName}</span>
                      <span className="block truncate text-xs text-slate-500">{candidate.email} · {candidate.organizationName}</span>
                      <span className="block truncate text-xs text-slate-400">
                        {typeof candidate.contentId === 'number'
                          ? `#${candidate.contentId} · ${candidate.contentTitle ?? '제목 없음'}`
                          : '현재 콘텐츠를 판정할 수 없습니다.'}
                      </span>
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-medium ${
                      status.tone === 'blocked'
                        ? 'bg-red-400/10 text-red-300'
                        : status.tone === 'replace'
                          ? 'bg-blue-400/10 text-blue-300'
                          : 'bg-emerald-400/10 text-emerald-300'
                    }`}>
                      {status.label}
                    </span>
                  </button>
                )
              })
            )}
          </div>

          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">플랜 레벨</p>
            {selectedCandidate === null ? (
              <p className="rounded-xl border border-dashed border-slate-700 px-4 py-4 text-center text-xs text-slate-500">원생을 선택하면 현재 과정의 플랜 레벨이 표시됩니다.</p>
            ) : null}
            <div className="grid grid-cols-3 gap-2">
              {eligiblePlans.map((plan) => (
                <button
                  key={plan.level}
                  type="button"
                  onClick={() => setSelectedLevel(plan.level)}
                  className={`rounded-xl border p-3 text-left transition ${
                    selectedLevel === plan.level
                      ? 'border-amber-400 bg-amber-400/10'
                      : 'border-slate-700 bg-slate-900 hover:border-slate-600'
                  }`}
                >
                  <span className="block text-sm font-bold text-slate-100">{plan.level}</span>
                  <span className="mt-1 block text-xs text-slate-500">{plan.grantedLessons}개 레슨</span>
                </button>
              ))}
            </div>

          </div>
        </div>

        <div className="p-6">
          <div className="mb-5 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-xs font-bold text-slate-950">2</span>
            <h3 className="font-semibold">확인 후 발급</h3>
          </div>

          {selectedCandidate && selectedPlan ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-800 bg-slate-800 text-sm">
                <div className="bg-slate-900 p-3"><p className="text-xs text-slate-500">원생</p><p className="mt-1 font-medium">{selectedCandidate.fullName} <span className="text-slate-500">#{selectedCandidate.userId}</span></p></div>
                <div className="bg-slate-900 p-3"><p className="text-xs text-slate-500">조직</p><p className="mt-1 font-medium">{selectedCandidate.organizationName}</p></div>
                <div className="bg-slate-900 p-3"><p className="text-xs text-slate-500">콘텐츠</p><p className="mt-1 font-medium">#{selectedCandidate.contentId} · {selectedCandidate.contentTitle ?? '제목 없음'}</p></div>
                <div className="bg-slate-900 p-3"><p className="text-xs text-slate-500">권한</p><p className="mt-1 font-medium">{selectedPlan.level} · {selectedPlan.grantedLessons}개 · 발급일 포함 35일</p></div>
              </div>

              {selectedCandidate.activeEntitlementSource === 'ADMIN_TEST' ? (
                <div className="flex gap-2 rounded-lg border border-blue-400/20 bg-blue-400/10 px-3 py-2 text-xs text-blue-200">
                  <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
                  기존 테스트권을 회수한 뒤 새 플랜으로 다시 발급합니다.
                </div>
              ) : null}

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-slate-400">발급 사유</span>
                <textarea
                  value={grantReason}
                  onChange={(event) => setGrantReason(event.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="예: 2026-07 상용 학습 회귀 테스트"
                  className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none placeholder:text-slate-600 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-medium text-slate-400">
                  확인 문구 <code className="rounded bg-slate-800 px-1.5 py-0.5 text-amber-300">{expectedConfirmation}</code>
                </span>
                <input
                  value={confirmationText}
                  onChange={(event) => setConfirmationText(event.target.value)}
                  placeholder={expectedConfirmation}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-mono outline-none placeholder:text-slate-600 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                />
              </label>

              <button
                type="button"
                disabled={!canGrant}
                onClick={() => grantMutation.mutate()}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-400 px-4 py-3 text-sm font-bold text-slate-950 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-35"
              >
                {grantMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserRoundCheck className="h-4 w-4" />}
                테스트 학습권 발급
              </button>
            </div>
          ) : (
            <div className="flex min-h-72 flex-col items-center justify-center rounded-xl border border-dashed border-slate-700 bg-slate-900/40 px-6 text-center">
              <UserRoundCheck className="h-8 w-8 text-slate-700" />
              <p className="mt-3 text-sm font-medium text-slate-400">원생과 플랜을 선택해주세요.</p>
              <p className="mt-1 text-xs text-slate-600">선택 정보와 만료일을 확인한 뒤 발급할 수 있습니다.</p>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-800 bg-slate-900/50 px-6 py-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-amber-300" />
            <h3 className="text-sm font-semibold">현재 active 테스트권</h3>
          </div>
          <span className="text-xs text-slate-500">{entitlementsQuery.data?.length ?? 0}건</span>
        </div>

        {entitlementsQuery.isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-amber-300" /></div>
        ) : entitlementsQuery.isError ? (
          <p className="py-6 text-center text-sm text-red-300">테스트권 목록을 불러오지 못했습니다.</p>
        ) : (entitlementsQuery.data ?? []).length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-700 py-6 text-center text-sm text-slate-500">active 테스트권이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full min-w-[60rem] text-sm">
              <thead className="bg-slate-900 text-left text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">원생 / 조직</th>
                  <th className="px-4 py-2.5 font-medium">콘텐츠</th>
                  <th className="px-4 py-2.5 font-medium">플랜</th>
                  <th className="px-4 py-2.5 font-medium">잔여</th>
                  <th className="px-4 py-2.5 font-medium">절대 만료</th>
                  <th className="px-4 py-2.5 font-medium">사유</th>
                  <th className="px-4 py-2.5 text-right font-medium">작업</th>
                </tr>
              </thead>
              <tbody>
                {(entitlementsQuery.data ?? []).map((entitlement) => (
                  <tr key={entitlement.entitlementId} className="border-t border-slate-800 bg-slate-950/60">
                    <td className="px-4 py-3"><p className="font-medium">{entitlement.fullName} <span className="text-slate-600">#{entitlement.userId}</span></p><p className="text-xs text-slate-500">{entitlement.organizationName} · {entitlement.email}</p></td>
                    <td className="max-w-56 px-4 py-3 text-slate-300"><span className="line-clamp-2">#{entitlement.contentId} · {entitlement.contentTitle ?? '제목 없음'}</span></td>
                    <td className="px-4 py-3 font-medium text-amber-300">{entitlement.level}</td>
                    <td className="px-4 py-3">{entitlement.remainingLessons} / {entitlement.grantedLessons}</td>
                    <td className="px-4 py-3">{formatDate(entitlement.adminGrantExpiresOn)}</td>
                    <td className="max-w-64 px-4 py-3 text-slate-400"><span className="line-clamp-2">{entitlement.grantReason}</span></td>
                    <td className="px-4 py-3 text-right"><button type="button" onClick={() => { setRevokeTarget(entitlement); setRevokeReason('') }} className="rounded-lg border border-red-400/20 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-400/10">회수</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {revokeTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" onClick={() => setRevokeTarget(null)}>
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div><h3 className="font-semibold text-white">테스트 학습권 회수</h3><p className="mt-1 text-sm text-slate-400">{revokeTarget.fullName} · {revokeTarget.level} · 잔여 {revokeTarget.remainingLessons}개</p></div>
              <button type="button" onClick={() => setRevokeTarget(null)} className="rounded-lg p-1 text-slate-500 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className="mt-5 block"><span className="mb-1.5 block text-xs font-medium text-slate-400">회수 사유</span><textarea value={revokeReason} onChange={(event) => setRevokeReason(event.target.value)} maxLength={500} rows={3} autoFocus placeholder="예: 상용 학습 테스트 완료" className="w-full resize-none rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-red-400 focus:ring-2 focus:ring-red-400/20" /></label>
            <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setRevokeTarget(null)} className="rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-slate-800">취소</button><button type="button" disabled={!revokeReason.trim() || revokeMutation.isPending} onClick={() => revokeMutation.mutate({ entitlementId: revokeTarget.entitlementId, reason: revokeReason.trim() })} className="rounded-lg bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:bg-red-400 disabled:opacity-40">{revokeMutation.isPending ? '회수 중...' : '회수 확정'}</button></div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Loading() {
  return (
    <div className="flex items-center gap-2 py-6 text-slate-500">
      <Loader2 className="w-5 h-5 animate-spin" /> 불러오는 중...
    </div>
  )
}

function LoadError() {
  return (
    <div className="flex items-center gap-2 py-6 text-red-600">
      <AlertCircle className="w-5 h-5" /> 데이터를 불러오지 못했습니다.
    </div>
  )
}

export function LcContentFeeConfigPage() {
  const [selected, setSelected] = useState<SelectedContent | null>(null)
  const effectivePlansQuery = useQuery({
    queryKey: EFFECTIVE_PLANS_QK,
    queryFn: () => lcContentFeeApi.getEffectivePlans(),
    select: (res) => res.data.data ?? [],
  })
  const curricula = effectivePlansQuery.data ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">LC 콘텐츠 사용료</h1>
        <p className="text-sm text-slate-500">
          콘텐츠별 레벨 플랜을 설정하고, 상용 테스트 학습권을 발급합니다.
        </p>
      </div>

      <Tabs defaultValue="plans">
        <TabsList variant="line">
          <TabsTrigger value="plans">
            <CreditCard /> 콘텐츠 플랜 설정
          </TabsTrigger>
          <TabsTrigger value="test-entitlements">
            <ShieldCheck /> 테스트 학습권 발급
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="pt-4">
          <p className="mb-4 text-sm text-slate-500">
            과정(커리큘럼)에 기본값을 두고, 값이 다른 코스에만 코스 예외를 설정합니다. 레벨(L3/L4/L5)별 가격·레슨수를 입력합니다.
          </p>
          <div className="mb-6">
            <EffectivePlanOverview
              curricula={curricula}
              isLoading={effectivePlansQuery.isLoading}
              isError={effectivePlansQuery.isError}
              selectedId={selected?.id ?? null}
              onSelect={setSelected}
            />
          </div>
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[20rem_minmax(0,1fr)_24rem]">
            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3 text-amber-600">
                <FolderOpen className="w-5 h-5" />
                <h2 className="text-base font-semibold text-slate-900">콘텐츠 선택</h2>
              </div>
              <ContentPicker selected={selected} onSelect={setSelected} />
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4 text-amber-600">
                <CreditCard className="w-5 h-5" />
                <h2 className="text-lg font-semibold text-slate-900">레벨별 플랜</h2>
              </div>
              {selected ? (
                <PlanEditor key={selected.id} content={selected} curricula={curricula} />
              ) : (
                <p className="py-12 text-center text-sm text-slate-400">
                  왼쪽에서 커리큘럼 또는 코스를 선택하세요.
                </p>
              )}
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-4">
              <TemplateManager selectedContent={selected} />
            </section>
          </div>
        </TabsContent>

        <TabsContent value="test-entitlements" className="pt-4">
          <TestEntitlementManager />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default LcContentFeeConfigPage
